import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Minus, Sparkles, Crown, Zap, Brain, Users, Clock, Globe, FileText, MessageSquare } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useThemeStore } from '../../store/theme';

// Competitor logos as simple styled components
const CompetitorLogo = ({ name, color }: { name: string; color: string }) => (
  <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center text-white font-bold text-sm`}>
    {name.charAt(0)}
  </div>
);

interface ComparisonFeature {
  category: string;
  feature: string;
  description: string;
  flowai: string | boolean;
  chatgpt: string | boolean;
  gemini: string | boolean;
  claude: string | boolean;
  copilot: string | boolean;
}

const comparisonData: ComparisonFeature[] = [
  // All-in-One Platform
  {
    category: 'All-in-One Platform',
    feature: '15 Specialized AI Employees',
    description: 'Pre-built AI specialists for marketing, HR, sales, support, etc.',
    flowai: true,
    chatgpt: false,
    gemini: false,
    claude: false,
    copilot: false,
  },
  {
    category: 'All-in-One Platform',
    feature: 'One-Click Task Templates',
    description: '100+ ready-to-use task templates for common business needs',
    flowai: '100+',
    chatgpt: false,
    gemini: false,
    claude: false,
    copilot: false,
  },
  {
    category: 'All-in-One Platform',
    feature: 'Team Collaboration',
    description: 'Share AI employees across your team with role-based access',
    flowai: true,
    chatgpt: 'Limited',
    gemini: 'Limited',
    claude: 'Limited',
    copilot: 'Limited',
  },
  {
    category: 'All-in-One Platform',
    feature: 'Integration Ecosystem',
    description: 'Connect with your existing tools and workflows',
    flowai: '50+',
    chatgpt: '~10',
    gemini: '~5',
    claude: '~5',
    copilot: '~15',
  },
  // Custom Training
  {
    category: 'Custom Training',
    feature: 'Brain AI (Custom Knowledge)',
    description: 'Train AI with your documents, websites, and data',
    flowai: true,
    chatgpt: 'GPTs only',
    gemini: false,
    claude: 'Projects',
    copilot: false,
  },
  {
    category: 'Custom Training',
    feature: 'Website Crawling',
    description: 'Automatically learn from your website content',
    flowai: true,
    chatgpt: false,
    gemini: false,
    claude: false,
    copilot: false,
  },
  {
    category: 'Custom Training',
    feature: 'Document Upload & Training',
    description: 'Upload PDFs, docs, and files to train AI',
    flowai: 'Unlimited',
    chatgpt: 'Limited',
    gemini: 'Limited',
    claude: 'Limited',
    copilot: 'Limited',
  },
  {
    category: 'Custom Training',
    feature: 'Custom Instructions',
    description: 'Define specific behavior and response styles',
    flowai: 'Per Employee',
    chatgpt: 'Global only',
    gemini: 'Basic',
    claude: 'Projects',
    copilot: 'Basic',
  },
  // Output Customization
  {
    category: 'Customized Output',
    feature: 'Role-Specific Responses',
    description: 'AI responds based on specialized role and expertise',
    flowai: true,
    chatgpt: 'Manual',
    gemini: 'Manual',
    claude: 'Manual',
    copilot: 'Manual',
  },
  {
    category: 'Customized Output',
    feature: 'Brand Voice & Tone',
    description: 'Maintain consistent brand voice across all outputs',
    flowai: true,
    chatgpt: 'Manual',
    gemini: 'Manual',
    claude: 'Manual',
    copilot: 'Manual',
  },
  {
    category: 'Customized Output',
    feature: 'Output Templates',
    description: 'Pre-formatted output templates for consistent results',
    flowai: true,
    chatgpt: false,
    gemini: false,
    claude: false,
    copilot: false,
  },
  {
    category: 'Customized Output',
    feature: 'Multi-Format Export',
    description: 'Export to different formats (Word, PDF, HTML, etc.)',
    flowai: true,
    chatgpt: 'Copy only',
    gemini: 'Copy only',
    claude: 'Copy only',
    copilot: 'Copy only',
  },
  // Automation
  {
    category: 'Automation',
    feature: '24/7 Scheduled Tasks',
    description: 'Schedule recurring AI tasks that run automatically',
    flowai: true,
    chatgpt: false,
    gemini: false,
    claude: false,
    copilot: false,
  },
  {
    category: 'Automation',
    feature: 'Workflow Automation',
    description: 'Chain multiple AI tasks together',
    flowai: true,
    chatgpt: false,
    gemini: false,
    claude: false,
    copilot: 'Basic',
  },
  {
    category: 'Automation',
    feature: 'API Access',
    description: 'Integrate AI capabilities into your own apps',
    flowai: true,
    chatgpt: true,
    gemini: true,
    claude: true,
    copilot: 'Limited',
  },
  // Language & Localization
  {
    category: 'Global Reach',
    feature: 'Multi-Language Support',
    description: 'Work in multiple languages seamlessly',
    flowai: '100+',
    chatgpt: '~80',
    gemini: '~40',
    claude: '~80',
    copilot: '~50',
  },
  {
    category: 'Global Reach',
    feature: 'Language-Specific Employees',
    description: 'AI employees specialized for specific languages',
    flowai: true,
    chatgpt: false,
    gemini: false,
    claude: false,
    copilot: false,
  },
];

const competitors = [
  { id: 'flowai', name: 'SweetPilot', color: 'from-purple-500 to-cyan-500', isUs: true },
  { id: 'chatgpt', name: 'ChatGPT', color: 'bg-emerald-600' },
  { id: 'gemini', name: 'Gemini', color: 'bg-blue-500' },
  { id: 'claude', name: 'Claude', color: 'bg-orange-500' },
  { id: 'copilot', name: 'Copilot', color: 'bg-sky-500' },
];

const categories = [...new Set(comparisonData.map(item => item.category))];

const categoryIcons: Record<string, typeof Sparkles> = {
  'All-in-One Platform': Sparkles,
  'Custom Training': Brain,
  'Customized Output': FileText,
  'Automation': Zap,
  'Global Reach': Globe,
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const renderValue = (value: string | boolean, isUs: boolean = false, isDark: boolean = true) => {
  if (typeof value === 'boolean') {
    return value ? (
      <Check className={`w-5 h-5 ${isUs ? 'text-green-400' : 'text-green-400/60'} mx-auto`} />
    ) : (
      <X className={`w-5 h-5 mx-auto ${isDark ? 'text-red-400/60' : 'text-red-400/80'}`} />
    );
  }
  return (
    <span className={`text-sm font-medium ${isUs ? 'text-green-400' : (isDark ? 'text-gray-400' : 'text-slate-500')}`}>
      {value}
    </span>
  );
};

export default function CompetitorComparison() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const filteredData = selectedCategory
    ? comparisonData.filter(item => item.category === selectedCategory)
    : comparisonData;

  const displayData = showAll ? filteredData : filteredData.slice(0, 8);

  return (
    <section className="py-24 lg:py-32 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6"
          >
            <Crown className="w-4 h-4 text-purple-400 mr-2" />
            <span className="text-sm text-purple-400">The All-in-One AI Platform</span>
          </motion.div>

          <motion.h2
            variants={fadeInUp}
            className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            SweetPilot vs{' '}
            <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-cyan-400 bg-clip-text text-transparent">
              ChatGPT, Gemini & Others
            </span>
          </motion.h2>

          <motion.p
            variants={fadeInUp}
            className={`text-lg max-w-3xl mx-auto mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}
          >
            Why settle for generic AI chatbots when you can have a complete AI workforce?
            SweetPilot combines everything in one platform with customized outputs tailored to your business.
          </motion.p>

          {/* Key Differentiators */}
          <motion.div
            variants={fadeInUp}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {[
              { icon: Users, text: '15 AI Employees vs 1 Chatbot' },
              { icon: Brain, text: 'Custom Training Built-in' },
              { icon: Clock, text: '24/7 Automation' },
              { icon: FileText, text: 'Tailored Outputs' },
            ].map((item, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border ${isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}
              >
                <item.icon className="w-4 h-4 text-cyan-400" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{item.text}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          className="flex flex-wrap justify-center gap-3 mb-8"
        >
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedCategory === null
                ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white'
                : isDark
                  ? 'bg-dark-800/50 text-gray-400 hover:text-white border border-white/10'
                  : 'bg-white text-slate-500 hover:text-slate-900 border border-slate-200 shadow-sm'
            }`}
          >
            All Features
          </button>
          {categories.map(category => {
            const Icon = categoryIcons[category] || Sparkles;
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white'
                    : isDark
                      ? 'bg-dark-800/50 text-gray-400 hover:text-white border border-white/10'
                      : 'bg-white text-slate-500 hover:text-slate-900 border border-slate-200 shadow-sm'
                }`}
              >
                <Icon className="w-4 h-4" />
                {category}
              </button>
            );
          })}
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          className={`rounded-2xl backdrop-blur-sm overflow-hidden border ${isDark ? 'bg-dark-800/30 border-white/10' : 'bg-white border-slate-200 shadow-lg'}`}
        >
          {/* Mobile: Card-based layout */}
          <div className="md:hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedCategory || 'all'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="divide-y divide-white/5"
              >
                {displayData.map((item, index) => (
                  <motion.div
                    key={`mobile-${item.category}-${item.feature}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`p-4 ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                  >
                    <div className="mb-3">
                      <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {item.feature}
                      </div>
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        {item.description}
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {competitors.map(comp => (
                        <div key={comp.id} className="text-center">
                          <div className="mb-1">
                            {comp.isUs ? (
                              <div className={`w-6 h-6 mx-auto rounded-lg bg-gradient-to-br ${comp.color} flex items-center justify-center`}>
                                <Sparkles className="w-3 h-3 text-white" />
                              </div>
                            ) : (
                              <div className={`w-6 h-6 mx-auto rounded-lg ${comp.color} flex items-center justify-center text-white text-xs font-bold`}>
                                {comp.name.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className={`${comp.isUs ? 'bg-gradient-to-r from-purple-500/10 to-cyan-500/10' : ''} rounded p-1`}>
                            {renderValue(item[comp.id as keyof ComparisonFeature] as string | boolean, comp.isUs, isDark)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Desktop: Table layout */}
          <div className="hidden md:block">
            {/* Table Header */}
            <div className={`grid grid-cols-7 gap-2 p-4 border-b sticky top-0 z-10 ${isDark ? 'border-white/10 bg-dark-700/50' : 'border-slate-200 bg-slate-50'}`}>
              <div className={`col-span-2 font-medium text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>Feature</div>
              {competitors.map(comp => (
                <div key={comp.id} className="text-center">
                  {comp.isUs ? (
                    <div className="inline-flex items-center justify-center gap-1.5">
                      <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${comp.color} flex items-center justify-center`}>
                        <Sparkles className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className={`text-xs font-bold hidden lg:inline ${isDark ? 'text-white' : 'text-slate-500'}`}>{comp.name}</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center justify-center gap-1.5">
                      <CompetitorLogo name={comp.name} color={comp.color} />
                      <span className={`text-xs hidden lg:inline ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{comp.name}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Table Body */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedCategory || 'all'}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {displayData.map((item, index) => (
                  <motion.div
                    key={`${item.category}-${item.feature}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`grid grid-cols-7 gap-2 p-4 border-b last:border-0 transition-colors group ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-slate-100 hover:bg-slate-50'}`}
                  >
                    <div className="col-span-2">
                      <div className={`text-sm font-medium group-hover:text-purple-400 transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {item.feature}
                      </div>
                      <div className={`text-xs mt-0.5 hidden lg:block ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                        {item.description}
                      </div>
                    </div>
                    <div className="flex items-center justify-center bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-lg">
                      {renderValue(item.flowai, true, isDark)}
                    </div>
                    <div className="flex items-center justify-center">
                      {renderValue(item.chatgpt, false, isDark)}
                    </div>
                    <div className="flex items-center justify-center">
                      {renderValue(item.gemini, false, isDark)}
                    </div>
                    <div className="flex items-center justify-center">
                      {renderValue(item.claude, false, isDark)}
                    </div>
                    <div className="flex items-center justify-center">
                      {renderValue(item.copilot, false, isDark)}
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Show More Button */}
          {filteredData.length > 8 && (
            <div className={`p-4 border-t text-center ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <button
                onClick={() => setShowAll(!showAll)}
                className="text-sm text-purple-400 hover:text-purple-300 font-medium"
              >
                {showAll ? 'Show Less' : `Show All ${filteredData.length} Features`}
              </button>
            </div>
          )}
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6 mt-12"
        >
          {[
            {
              title: 'All-in-One Platform',
              description: 'Stop paying for 5+ different AI tools. SweetPilot replaces ChatGPT, Jasper, Copy.ai, and more with one unified platform.',
              icon: Sparkles,
              color: 'from-purple-500 to-violet-600',
              stat: '5x',
              statLabel: 'tools replaced',
            },
            {
              title: 'Customized Outputs',
              description: 'Unlike generic chatbots, SweetPilot learns your brand voice, uses your data, and produces outputs tailored to your business.',
              icon: Brain,
              color: 'from-cyan-500 to-blue-600',
              stat: '100%',
              statLabel: 'customizable',
            },
            {
              title: 'True Automation',
              description: 'Other AI tools require you to be there. SweetPilot works 24/7 with scheduled tasks, workflows, and integrations.',
              icon: Zap,
              color: 'from-green-500 to-emerald-600',
              stat: '24/7',
              statLabel: 'automation',
            },
          ].map((card, index) => (
            <motion.div
              key={card.title}
              variants={fadeInUp}
              className={`relative p-6 rounded-2xl backdrop-blur-sm group transition-all border ${isDark ? 'bg-dark-800/50 border-white/10 hover:border-white/20' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'}`}
            >
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4`}>
                <card.icon className="w-6 h-6 text-white" />
              </div>
              <div className="relative">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className={`text-3xl font-bold bg-gradient-to-r ${card.color} bg-clip-text text-transparent`}>
                    {card.stat}
                  </span>
                  <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{card.statLabel}</span>
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{card.title}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>{card.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          className="text-center mt-12"
        >
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium text-lg shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-all hover:scale-105"
          >
            <span>Get Started</span>
            <Sparkles className="w-5 h-5" />
          </Link>
          <p className={`text-sm mt-3 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>No credit card required. Start with 15 AI employees today.</p>
        </motion.div>
      </div>
    </section>
  );
}
